import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Typography } from "@material-ui/core";
import { qlinkExpiredDailyPath } from "../../utils/daily-question-opaque";
import { forgetQLinkShareToken, qlinkIdFromPath } from "../../utils/qlink-share-token";
import { gtmTrack } from "../../utils/tracking";

// A full page load, so the Daily page lands like any campaign link: its
// first-touch capture and the analytics tags read the utm keys from the address.
// Same-origin path built from constants only (qlinkExpiredDailyPath).
export const goToExpiredDestination = (path) => {
  window.location.replace(path);
};

// The dead link's share token is of no further use: drop it (the shared key
// and this tab's copy for the link) before leaving.
const forgetDeadShareToken = () => forgetQLinkShareToken(qlinkIdFromPath(window.location.pathname));

// Honest states for a shared question that cannot load (first-experience
// contract). The old copy said "expired" for every failure, including the
// common case of a copied/reshared URL whose share token stayed behind in the
// first browser. Only a 404 (the server's "expired or gone") says expired;
// never shows a raw status.
//
// `expired`: the API said this was a real daily-post link that expired or was
// withdrawn (it says so only to the link's own share token). Instead of a dead
// end, the visitor goes on to today's case on the Daily ABSITE page.
const NEEDS_ORIGINAL_LINK = new Set([400, 401, 403]);

const QLinkLinkError = ({ status, expired = false, onRetry, navigate = goToExpiredDestination }) => {
  const history = useHistory();
  const code = Number(status);
  const needsOriginalLink = !status || NEEDS_ORIGINAL_LINK.has(code);
  const unavailable = code === 404;
  const destinationPath = unavailable && expired ? qlinkExpiredDailyPath(window.location.search) : null;

  useEffect(() => {
    if (!destinationPath) return;
    try {
      const platform = new URL(destinationPath, window.location.origin).searchParams.get("platform");
      gtmTrack("qlink_expired_redirect", { platform: platform || "unknown" });
    } catch (e) {
      // Tracking never blocks the way on.
    }
    forgetDeadShareToken();
    navigate(destinationPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationPath]);

  if (destinationPath) {
    return (
      <Box className="qlink-link-error" role="status" style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "100vh", padding: "24px", textAlign: "center", gap: "12px",
      }}>
        <Typography variant="h6" component="h1">This shared case has closed</Typography>
        <Typography style={{ maxWidth: 440 }}>Taking you to today’s ABSITE case…</Typography>
        <Button variant="contained" color="primary" style={{ textTransform: "none" }}
          onClick={() => { forgetDeadShareToken(); navigate(destinationPath); }}>
          Open today’s case
        </Button>
      </Box>
    );
  }

  let title = "We couldn't load this question";
  let body = "Something went wrong on our side. Please try again.";
  if (needsOriginalLink) {
    title = "Open the original link to see this question";
    body = "Shared questions open from the link you were sent, in the browser where you first opened it. "
      + "Tap that link again, or sign in free to keep practicing.";
  } else if (unavailable) {
    // 404 = the share link expired (the common case: shared links carry an
    // expiry) or was withdrawn. Re-tapping it can never work, so say so and
    // point somewhere that does.
    title = "This link has expired";
    body = "Shared questions are only open for a limited time. "
      + "Sign in free to keep practicing with QLearn's question bank.";
  }

  const retry = () => {
    if (typeof onRetry === "function") {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <Box className="qlink-link-error" role="alert" style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", padding: "24px", textAlign: "center", gap: "12px",
    }}>
      <Typography variant="h6" component="h1">{title}</Typography>
      <Typography style={{ maxWidth: 440 }}>{body}</Typography>
      {needsOriginalLink || unavailable ? (
        <Button variant="contained" color="primary" style={{ textTransform: "none" }}
          onClick={() => history.push("/login")}>
          Sign in to keep practicing
        </Button>
      ) : (
        <Button variant="contained" color="primary" style={{ textTransform: "none" }} onClick={retry}>
          Try again
        </Button>
      )}
    </Box>
  );
};

export default QLinkLinkError;
